import React from "react";
import { Button, Dropdown, Modal, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../configureStore";
import slice from "../../../containers/App/reducer";
import { WorkCompanyDto } from "../../../dto/User/WorkCompanyDto";
import { listenForWorkCompanyChange } from "../../../containers/App/workCompanyTabs";
import { getWorkCompanyLandingPath } from "../../../containers/App/workCompanyNavigation";

export default function WorkCompanySwitcher() {
  const dispatch = useDispatch();
  const { t } = useTranslation("translations");
  const user = useSelector((state: RootState) => state.global.userInfo);
  const host = useSelector((state: RootState) => state.global.host);
  const companies = user?.workCompanies ?? [];
  const homeCompany = companies.find((company) => company.homeCompany);
  const standaloneUaeAccount = homeCompany
    ? homeCompany.officeType === "UAE_OFFICE"
    : user?.company.settings.officeType === "UAE_OFFICE" && (!user.homeCompanyId || user.homeCompanyId === user.company.id);
  const status = useSelector((state: RootState) => state.global.workCompanySwitchStatus);
  const [selected, setSelected] = React.useState<WorkCompanyDto>();

  React.useEffect(() => {
    if (!user?.id || !user.contextVersion) return;
    return listenForWorkCompanyChange(host, user.id, user.contextVersion, () => dispatch(slice.actions.switchWorkCompanyReject()));
  }, [dispatch, host, user?.id, user?.contextVersion]);

  if (!user) return null;

  const pending = status === "pending" || status === "success";
  const failed = status === "error";
  const confirm = () => {
    if (selected && !pending && !failed) dispatch(slice.actions.switchWorkCompany(selected.companyId));
  };

  return (
    <>
      {Boolean(user.contextVersion) &&
        companies.length > 0 &&
        !standaloneUaeAccount &&
        (companies.length > 1 ? (
          <Dropdown
            disabled={pending || failed}
            trigger={["click"]}
            menu={{
              selectedKeys: [String(user.company.id)],
              items: companies.map((company) => ({
                key: String(company.companyId),
                label: company.companyName,
                disabled: company.companyId === user.company.id,
                onClick: () => setSelected(company),
              })),
            }}
          >
            <Button type="text" loading={pending} aria-label={`${t("company")}: ${user.company.name}`}>
              <Typography.Text ellipsis={{ tooltip: user.company.name }} style={{ maxWidth: 150 }}>
                {user.company.name}
              </Typography.Text>
              <DownOutlined aria-hidden />
            </Button>
          </Dropdown>
        ) : (
          <Typography.Text ellipsis={{ tooltip: user.company.name }} style={{ maxWidth: 150 }}>
            {user.company.name}
          </Typography.Text>
        ))}
      <Modal
        open={Boolean(selected) || failed}
        title={failed ? t("error") : `${t("company")}: ${selected?.companyName ?? ""}`}
        onOk={failed ? () => window.location.replace(getWorkCompanyLandingPath(host, window.location.search)) : confirm}
        onCancel={() => setSelected(undefined)}
        okText={failed ? t("refresh") : t("yes")}
        cancelText={t("cancel")}
        cancelButtonProps={{ disabled: pending || failed }}
        confirmLoading={pending}
        closable={!pending && !failed}
        keyboard={!pending && !failed}
        mask={{ closable: !pending && !failed }}
      >
        {failed ? t("work_company_reload_required") : t("work_company_switch_confirmation")}
      </Modal>
    </>
  );
}
